/**
 * AdminUsers — founder lookup page at /admin/users.
 *
 * What it answers in under 30 seconds (founder's spec):
 *   • Who signed up?
 *   • Where did they come from?
 *   • Did they generate a routine?
 *   • Did they become Pro?
 *   • Can I watch their session replay?
 *
 * Columns (per founder ask):
 *   created_at, email, auth_method, referral_source,
 *   first_landing_page, onboarding_completed, routine_generated,
 *   mirror_opened, pro_status, clarity_session_url.
 *
 * Sorting: backend supports created_at | email | pro_status, asc/desc.
 * Paging: 50/page default, configurable up to 200.
 *
 * Clarity column: renders the deep-link button when the backend has
 * a session ID stored AND CLARITY_PROJECT_ID is set in backend/.env.
 * When project ID is missing, the column header shows a one-time
 * banner explaining the setup.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronUp,
  ChevronDown,
  ExternalLink,
  CheckCircle2,
  Circle,
  Loader2,
  Search,
  Crown,
  ShieldCheck,
  MailCheck,
  Trash2,
} from "lucide-react";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Yes = () => <CheckCircle2 className="h-4 w-4 text-[#9B6A6C]" aria-label="yes" />;
const No = () => <Circle className="h-3.5 w-3.5 text-[#C9BFB9]" aria-label="no" />;

const SortChevron = ({ active, dir }) => {
  if (!active) return null;
  return dir === "desc" ? (
    <ChevronDown className="h-3 w-3 inline-block ml-1" />
  ) : (
    <ChevronUp className="h-3 w-3 inline-block ml-1" />
  );
};

const AdminUsers = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState("");
  const [clarityProjectId, setClarityProjectId] = useState(null);
  const [accountAudit, setAccountAudit] = useState(null);
  const [startingReview, setStartingReview] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const cleanupMode = new URLSearchParams(window.location.search).get("cleanup") === "1";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`, {
        params: { sort, order, limit: PAGE_SIZE, offset },
        withCredentials: true,
      });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setClarityProjectId(r.data?.clarity_project_id || null);
      try {
        const audit = await axios.get(`${API}/admin/account-verification-audit`, {
          withCredentials: true,
        });
        setAccountAudit(audit.data || null);
      } catch {
        // The main founder lookup remains useful if the audit panel fails.
        setAccountAudit(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [sort, order, offset]);

  useEffect(() => { load(); }, [load]);

  const startVerificationReview = async () => {
    const summary = accountAudit?.summary;
    const ready = Math.max(
      0,
      (summary?.eligible_for_review || 0)
        - (summary?.review_pending || 0)
        - (summary?.review_quarantined || 0),
    );
    if (!ready || startingReview) return;
    const confirmed = window.confirm(
      `Send a fresh verification email to ${ready} unverified, no-card account${ready === 1 ? "" : "s"} and start their 7-day review window?`,
    );
    if (!confirmed) return;
    setStartingReview(true);
    try {
      const r = await axios.post(
        `${API}/admin/account-verification-audit/start`,
        { window_days: 7 },
        { withCredentials: true },
      );
      const sent = r.data?.sent || 0;
      const failed = r.data?.failed || 0;
      if (failed) {
        toast.warning(`${sent} sent; ${failed} could not be sent.`);
      } else {
        toast.success(`Verification review started for ${sent} accounts.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start the verification review");
    } finally {
      setStartingReview(false);
    }
  };

  const runTestCleanupDryRun = async () => {
    if (cleanupBusy) return;
    setCleanupBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/qa-cleanup/execute`,
        { apply: false },
        { withCredentials: true },
      );
      setCleanupReport(r.data || null);
      toast.success(
        `Found ${r.data?.matched_account_count || 0} disposable accounts and ${r.data?.matched_invite_count || 0} test invites.`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't analyze test records");
    } finally {
      setCleanupBusy(false);
    }
  };

  const applyTestCleanup = async () => {
    if (cleanupBusy || cleanupReport?.mode !== "dry_run") return;
    const accounts = cleanupReport?.matched_account_count || 0;
    const invites = cleanupReport?.matched_invite_count || 0;
    const confirmed = window.confirm(
      `Permanently delete ${accounts} disposable test account${accounts === 1 ? "" : "s"} and ${invites} test invite${invites === 1 ? "" : "s"}? Your two founder accounts and the App Store reviewer account will be preserved. A 30-day recovery backup is already stored.`,
    );
    if (!confirmed) return;
    setCleanupBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/qa-cleanup/execute`,
        { apply: true, confirm: "DELETE_QA_ACCOUNTS" },
        { withCredentials: true },
      );
      setCleanupReport(r.data || null);
      if (r.data?.verification?.clean) {
        toast.success("Disposable test records were deleted cleanly.");
      } else {
        toast.warning("Cleanup finished with records that still need review.");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete test records");
    } finally {
      setCleanupBusy(false);
    }
  };

  // eslint-disable-next-line
  const toggleSort = (key) => {
    const sameCol = key === sort;
    setSort(sameCol ? sort : key);
    setOrder(sameCol && order === "desc" ? "asc" : "desc");
    setOffset(0);
  };

  // Client-side filter on top of the server result — keeps the founder
  // fast on a single page; emails are the most common search.
  const visibleRows = filter.trim()
    ? rows.filter((r) => {
        const q = filter.trim().toLowerCase();
        return (
          (r.email || "").toLowerCase().includes(q) ||
          (r.referral_source || "").toLowerCase().includes(q) ||
          (r.utm_source || "").toLowerCase().includes(q) ||
          (r.utm_campaign || "").toLowerCase().includes(q)
        );
      })
    : rows;

  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const readyForReview = Math.max(
    0,
    (accountAudit?.summary?.eligible_for_review || 0)
      - (accountAudit?.summary?.review_pending || 0)
      - (accountAudit?.summary?.review_quarantined || 0),
  );

  return (
    <AdminShell>
      <div
        className="max-w-7xl mx-auto px-6 sm:px-8 py-10"
        data-testid="admin-users-page"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#9B6A6C] mb-2">
              Founder lookup
            </div>
            <h1
              className="font-serif italic text-4xl text-[#2C2123] leading-tight"
              data-testid="admin-users-headline"
            >
              Users
            </h1>
            <p className="text-sm text-[#5C4B51] mt-2">
              {total.toLocaleString()} total · sorted by{" "}
              <span className="font-medium text-[#2C2123]">
                {sort.replace("_", " ")}
              </span>{" "}
              ({order})
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B6A6C]" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter this page…"
                className="pl-9 pr-4 py-2 rounded-full bg-white border border-[#EADDD7] text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#9B6A6C]/40"
                data-testid="admin-users-filter"
              />
            </div>
          </div>
        </div>

        {accountAudit?.summary && (
          <div
            className="mb-6 rounded-3xl border border-[#EADDD7] bg-white p-5"
            data-testid="account-verification-audit"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-[#2C2123] font-medium">
                  <ShieldCheck className="h-5 w-5 text-[#9B6A6C]" />
                  Account verification audit
                </div>
                <p className="mt-1 text-xs text-[#8A7D82]">
                  Protected accounts are excluded. Unverified accounts get a final warning after day 7, then a 30-day grace period before deletion.
                </p>
              </div>
              <button
                type="button"
                onClick={startVerificationReview}
                disabled={startingReview || readyForReview === 0}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9B6A6C] px-5 py-2.5 text-sm text-white disabled:opacity-50"
                data-testid="start-account-verification-review"
              >
                {startingReview ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MailCheck className="h-4 w-4" />
                )}
                Start 7-day review
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <AuditStat label="Card / paid" value={accountAudit.summary.card_verified} />
              <AuditStat label="Verified, no card" value={accountAudit.summary.verified_no_card} />
              <AuditStat label="Protected access" value={accountAudit.summary.trusted_entitlement} />
              <AuditStat label="Unverified, active" value={accountAudit.summary.unverified_with_activity} warn />
              <AuditStat label="Unverified, inactive" value={accountAudit.summary.unverified_no_activity} warn />
            </div>
            {(accountAudit.summary.review_pending > 0 || accountAudit.summary.review_quarantined > 0) && (
              <p className="mt-3 text-xs text-[#5C4B51]">
                {accountAudit.summary.review_pending} pending verification · {accountAudit.summary.review_quarantined} quarantined · {accountAudit.summary.deletion_scheduled || 0} scheduled for deletion
              </p>
            )}
          </div>
        )}

        {cleanupMode && (
          <div
            className="mb-6 rounded-3xl border border-rose-200 bg-white p-5"
            data-testid="test-data-cleanup"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-[#2C2123] font-medium">
                  <Trash2 className="h-5 w-5 text-rose-700" />
                  Disposable test-data cleanup
                </div>
                <p className="mt-1 text-xs text-[#8A7D82]">
                  Preserves iyal@trubeaute.com, iyalbasen@gmail.com, reviewer@trubeaute.com, and every account with live billing evidence.
                </p>
              </div>
              <button
                type="button"
                onClick={runTestCleanupDryRun}
                disabled={cleanupBusy}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#9B6A6C] px-5 py-2.5 text-sm text-[#5D4050] disabled:opacity-50"
                data-testid="analyze-test-data"
              >
                {cleanupBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Analyze safely
              </button>
            </div>

            {cleanupReport && (
              <div className="mt-4 rounded-2xl bg-[#FAF6F2] p-4" data-testid="test-cleanup-report">
                <p className="text-sm text-[#2C2123]">
                  <strong>{cleanupReport.matched_account_count || 0}</strong> disposable accounts ·{" "}
                  <strong>{cleanupReport.matched_invite_count || 0}</strong> test invites ·{" "}
                  <strong>{cleanupReport.preserved_by_reason?.always_keep_list || 0}</strong> approved accounts preserved
                </p>
                {cleanupReport.backup_id && (
                  <p className="mt-1 text-xs text-[#8A7D82]">
                    Recovery backup {cleanupReport.backup_id} is retained for 30 days.
                  </p>
                )}
                {cleanupReport.mode === "dry_run" && cleanupReport.matched_count > 0 && (
                  <button
                    type="button"
                    onClick={applyTestCleanup}
                    disabled={cleanupBusy}
                    className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-rose-700 px-5 py-2.5 text-sm text-white disabled:opacity-50"
                    data-testid="delete-test-data"
                  >
                    {cleanupBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Delete these test records
                  </button>
                )}
                {cleanupReport.mode === "apply" && (
                  <p className={`mt-3 text-sm font-medium ${cleanupReport.verification?.clean ? "text-emerald-700" : "text-amber-700"}`}>
                    {cleanupReport.verification?.clean
                      ? "Cleanup verified: no disposable test accounts, test invites, or linked orphan records remain."
                      : "Cleanup completed with residue; review the verification details before disabling the endpoint."}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {clarityProjectId == null && (
          <div
            className="mb-5 px-4 py-3 bg-[#FFF5EB] border border-[#F3DDC4] rounded-2xl text-sm text-[#7A5230] flex items-start gap-2"
            data-testid="admin-users-clarity-banner"
          >
            <span className="font-medium">Clarity not yet configured.</span>
            <span>
              Set <code className="bg-white/60 px-1.5 py-0.5 rounded text-[12px]">
                CLARITY_PROJECT_ID
              </code> in <code className="bg-white/60 px-1.5 py-0.5 rounded text-[12px]">
                /app/backend/.env
              </code>{" "}
              and redeploy to enable session-replay deep-links here.
            </span>
          </div>
        )}

        <div className="bg-white border border-[#EADDD7] rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAF6F2] text-[11px] uppercase tracking-[0.16em] text-[#9B6A6C]">
                  <Th onClick={() => toggleSort("created_at")} active={sort === "created_at"} dir={order}>
                    Signed up
                  </Th>
                  <Th onClick={() => toggleSort("email")} active={sort === "email"} dir={order}>
                    Email
                  </Th>
                  <Th>Method</Th>
                  <Th>Referrer</Th>
                  <Th>Landing</Th>
                  <Th title="Completed onboarding questionnaire">Onb.</Th>
                  <Th title="Generated at least one routine">Routine</Th>
                  <Th title="Opened the AR mirror at least once">Mirror</Th>
                  <Th onClick={() => toggleSort("pro_status")} active={sort === "pro_status"} dir={order}>
                    Pro
                  </Th>
                  <Th>Clarity</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-[#8A7D82]">
                      <Loader2 className="h-4 w-4 inline-block animate-spin mr-2" />
                      Loading users…
                    </td>
                  </tr>
                )}
                {!loading && visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-[#8A7D82] italic">
                      No users match this view.
                    </td>
                  </tr>
                )}
                {!loading && visibleRows.map((u) => (
                  <UserRow key={u.user_id} u={u} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pager */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#EADDD7] bg-[#FBF8F4]">
            <div className="text-xs text-[#8A7D82]">
              {total > 0 ? `${pageStart}–${pageEnd} of ${total.toLocaleString()}` : "—"}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || loading}
                className="text-xs px-3 py-1.5 rounded-full border border-[#EADDD7] text-[#5C4B51] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-users-prev"
              >
                ← Newer
              </button>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={pageEnd >= total || loading}
                className="text-xs px-3 py-1.5 rounded-full border border-[#EADDD7] text-[#5C4B51] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-users-next"
              >
                Older →
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

const AuditStat = ({ label, value, warn = false }) => (
  <div className={`rounded-2xl px-4 py-3 ${warn ? "bg-amber-50" : "bg-[#FAF6F2]"}`}>
    <div className={`text-xl font-semibold ${warn ? "text-amber-900" : "text-[#2C2123]"}`}>
      {(value || 0).toLocaleString()}
    </div>
    <div className="mt-0.5 text-[11px] uppercase tracking-[0.1em] text-[#8A7D82]">
      {label}
    </div>
  </div>
);

const Th = ({ children, onClick, active, dir, title }) => (
  <th
    onClick={onClick}
    title={title}
    className={`text-left px-4 py-3 font-semibold whitespace-nowrap ${
      onClick ? "cursor-pointer hover:text-[#5D4050]" : ""
    }`}
  >
    {children}
    <SortChevron active={!!active} dir={dir} />
  </th>
);

const UserRow = ({ u }) => (
  <tr
    className="border-t border-[#F4ECE3] hover:bg-[#FBF8F4]"
    data-testid={`admin-users-row-${u.user_id}`}
  >
    <td className="px-4 py-3 text-[12px] text-[#5C4B51] whitespace-nowrap">
      {fmtDateTime(u.created_at)}
    </td>
    <td className="px-4 py-3 text-[#2C2123] font-medium max-w-[260px] truncate" title={u.email}>
      {u.email || <span className="text-[#8A7D82] italic">—</span>}
    </td>
    <td className="px-4 py-3 text-[#5C4B51] whitespace-nowrap">{u.auth_method}</td>
    <td className="px-4 py-3 text-[#5C4B51] max-w-[200px] truncate" title={u.http_referrer || u.referral_source}>
      {u.referral_source}
    </td>
    <td className="px-4 py-3 text-[12px] text-[#8A7D82] max-w-[180px] truncate" title={u.first_landing_page}>
      {u.first_landing_page || "/"}
    </td>
    <td className="px-4 py-3 text-center">{u.onboarding_completed ? <Yes /> : <No />}</td>
    <td className="px-4 py-3 text-center">{u.routine_generated ? <Yes /> : <No />}</td>
    <td className="px-4 py-3 text-center">{u.mirror_opened ? <Yes /> : <No />}</td>
    <td className="px-4 py-3 whitespace-nowrap">
      {u.pro_status === "Pro" ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#9B6A6C]/10 text-[#9B6A6C] text-[11px] font-medium">
          <Crown className="h-3 w-3" />
          {u.pro_plan || "Pro"}
        </span>
      ) : (
        <span className="text-[#8A7D82] text-[11px]">Free</span>
      )}
    </td>
    <td className="px-4 py-3 whitespace-nowrap">
      {u.clarity_session_url ? (
        <a
          href={u.clarity_session_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#9B6A6C]/10 text-[#9B6A6C] text-[11px] hover:bg-[#9B6A6C]/20"
          data-testid={`admin-users-clarity-link-${u.user_id}`}
        >
          Watch <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-[#8A7D82] text-[11px]">—</span>
      )}
    </td>
  </tr>
);

export default AdminUsers;
